import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { ImageUploader } from "./ImageUploader";

const EMPTY = { name: "", category: "", description: "", photos: [], price: "", original_price: "", shopee_link: "", tiktok_link: "" };

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-slate-700">{label}</Label>{children}</div>
);

export const ProductFormDialog = ({ open, onOpenChange, product, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(product ? { ...EMPTY, ...product, original_price: product.original_price ?? "" } : EMPTY);
    }
  }, [open, product]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: form.name, category: form.category, description: form.description, photos: form.photos,
      price: Number(form.price || 0), original_price: form.original_price === "" ? null : Number(form.original_price),
      shopee_link: form.shopee_link.trim(), tiktok_link: form.tiktok_link.trim(),
    };
    try {
      if (product) await api.put(`/products/${product.product_id}`, payload);
      else await api.post("/products", payload);
      toast.success(product ? "Produk diperbarui" : "Produk ditambahkan");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle className="text-xl">{product ? "Edit produk" : "Tambah produk"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Foto produk (bisa lebih dari 1)">
            <ImageUploader value={form.photos} onChange={(photos) => setForm((f) => ({ ...f, photos }))} testId="product-photos" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nama produk"><Input required value={form.name} onChange={set("name")} data-testid="product-name-input" /></Field>
            <Field label="Kategori"><Input value={form.category} onChange={set("category")} placeholder="mis. Sepatu" data-testid="product-category-input" /></Field>
            <Field label="Harga jual (Rp)"><Input required type="number" min="0" value={form.price} onChange={set("price")} data-testid="product-price-input" /></Field>
            <Field label="Harga coret / sebelum diskon (Rp)"><Input type="number" min="0" value={form.original_price} onChange={set("original_price")} data-testid="product-original-price-input" /></Field>
            <Field label="Link affiliate Shopee"><Input type="url" value={form.shopee_link} onChange={set("shopee_link")} placeholder="https://s.shopee.co.id/..." data-testid="product-shopee-input" /></Field>
            <Field label="Link affiliate TikTok"><Input type="url" value={form.tiktok_link} onChange={set("tiktok_link")} placeholder="https://vt.tokopedia.com/..." data-testid="product-tiktok-input" /></Field>
          </div>
          <p className="text-xs text-slate-500 -mt-2">Jika link Shopee/TikTok kosong, toko publik otomatis menampilkan tombol beli via WhatsApp (nomor toko).</p>
          <Field label="Deskripsi"><Textarea rows={3} value={form.description} onChange={set("description")} data-testid="product-description-input" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="product-form-cancel-button">Batal</Button>
            <Button type="submit" disabled={busy} data-testid="product-form-submit-button" className="rounded-xl">
              {busy ? "Menyimpan..." : "Simpan produk"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
